// Measurement entry fields in the account's units. On a metric account each
// field below shows and accepts metric, while its `.value` keeps reading and
// writing imperial, so all existing code that reads or fills these fields
// stays correct without change. On an imperial account a filled-in value is
// only rounded for display (roundImperial): a rig saved from metric is stored
// as its exact conversion (3.5 m = 11.48293963254593 ft), which the planner
// and profile used to show digit for digit.
import * as units from './units.js';

const native = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');

// How each kind of measurement is shown. Rig heights round up and limits
// (a campground's maximum rig length) round down, so rounding never makes a
// fit look roomier than it is.
const FT_TO_M = 0.3048, LB_TO_KG = 0.45359237, MPH_TO_KMH = 1.609344;
// Fixed metric <-> imperial conversions. They must not depend on the current
// unit setting: when an account switches back to imperial, fields still need
// to read their metric contents correctly while converting back.
const roundTo = (v, step, mode) => {
    const n = v / step;
    const r = mode === 'up' ? Math.ceil(n - 1e-9) : mode === 'down' ? Math.floor(n + 1e-9) : Math.round(n);
    return r * step;
};
const lengthKind = (mode) => ({
    show: (ft) => roundTo(ft * FT_TO_M, 0.1, mode), exact: (ft) => ft * FT_TO_M,
    toImperial: (m) => m / FT_TO_M, step: 0.1, decimals: 1,
    showImperial: (ft) => Math.round(ft * 100) / 100, imperialDecimals: 2
});
const KINDS = {
    height: lengthKind('up'),
    length: lengthKind('up'),
    limitLength: lengthKind('down'),
    clearance: lengthKind('down'),
    weight: { show: (lbs) => roundTo(lbs * LB_TO_KG, 10), exact: (lbs) => lbs * LB_TO_KG, toImperial: (kg) => kg / LB_TO_KG, step: 10, decimals: 0,
        showImperial: (lbs) => Math.round(lbs), imperialDecimals: 0 },
    speed: { show: (mph) => Math.round(mph * MPH_TO_KMH), exact: (mph) => mph * MPH_TO_KMH, toImperial: (kmh) => kmh / MPH_TO_KMH, step: 5, decimals: 0,
        showImperial: (mph) => Math.round(mph), imperialDecimals: 0 }
};

const FIELDS = {
    // route planner (app.html)
    height: 'height', length: 'length', weight: 'weight',
    towCapacity: 'weight', towVehicleWeight: 'weight',
    'cgf-maxrig': 'limitLength', 'rr-clearance': 'clearance',
    // profile page
    rvHeight: 'height', rvLength: 'length', rvWeight: 'weight',
    avgCruisingSpeed: 'speed', maxSpeed: 'speed',
    // onboarding
    'w-height': 'height', 'w-length': 'length', 'w-weight': 'weight', 'w-speed': 'speed'
};

const LABEL_UNITS = [[/\b(feet|ft)\b/g, 'm'], [/\blbs\b/g, 'kg'], [/\bmph\b/g, 'km/h']];
// Original label text, restored exactly when a field goes back to imperial.
const originalLabelText = new WeakMap();

const fmt = (n, decimals) => String(Number(n.toFixed(decimals)));

// Converts every listed field on the page to the current unit system, or back.
// Safe to call again whenever the units change (e.g. once the profile loads).
export function syncInputUnits(root = document) {
    for (const [id, kindName] of Object.entries(FIELDS)) {
        const el = root.getElementById ? root.getElementById(id) : root.querySelector(`#${CSS.escape(id)}`);
        if (!el) continue;
        if (units.isMetric()) toMetric(el, KINDS[kindName]);
        else toImperial(el, KINDS[kindName]);
    }
}

function toMetric(el, kind) {
    if (el.dataset.metric === 'on') return;
    // The exact value, not the rounded display, if roundImperial has the field.
    const imperial = el.dataset.imperialRound === 'on' ? el.value : native.get.call(el);
    delete el.dataset.imperialRound;
    el.dataset.metric = 'on';
    el.dataset.imperialAttrs = JSON.stringify({
        min: el.getAttribute('min'), max: el.getAttribute('max'),
        step: el.getAttribute('step'), placeholder: el.getAttribute('placeholder')
    });
    // Limits convert exactly, then widen outward to the field's step, so every
    // value that was valid in imperial stays valid in metric.
    const limit = (attr, round) => {
        const v = el.getAttribute(attr);
        if (v === null || v === '' || isNaN(v)) return;
        const steps = round(kind.exact(Number(v)) / kind.step - (round === Math.ceil ? 1e-9 : -1e-9));
        el.setAttribute(attr, fmt(steps * kind.step, kind.decimals));
    };
    limit('min', Math.floor);
    limit('max', Math.ceil);
    el.setAttribute('step', String(kind.step));
    const ph = el.getAttribute('placeholder');
    if (ph) el.setAttribute('placeholder', ph.replace(/\d[\d,]*(\.\d+)?/g, (n) => fmt(kind.show(Number(n.replace(/,/g, ''))), kind.decimals)));

    Object.defineProperty(el, 'value', {
        configurable: true,
        get() {
            const v = native.get.call(this);
            return v === '' || isNaN(parseFloat(v)) ? v : String(kind.toImperial(parseFloat(v)));
        },
        set(v) {
            native.set.call(this, v === '' || v === null || v === undefined || isNaN(parseFloat(v))
                ? v ?? '' : fmt(kind.show(parseFloat(v)), kind.decimals));
        }
    });
    if (imperial !== '') el.value = imperial;
    relabel(el);
}

function toImperial(el, kind) {
    if (el.dataset.metric !== 'on') {
        roundImperial(el, kind);
        return;
    }
    const imperial = el.value;
    delete el.value; // drop the instance override; the prototype's value applies again
    const attrs = JSON.parse(el.dataset.imperialAttrs || '{}');
    for (const [k, v] of Object.entries(attrs)) {
        if (v === null) el.removeAttribute(k); else el.setAttribute(k, v);
    }
    delete el.dataset.metric;
    delete el.dataset.imperialAttrs;
    native.set.call(el, '');
    for (const node of labelTextNodes(el)) {
        if (originalLabelText.has(node)) node.nodeValue = originalLabelText.get(node);
    }
    roundImperial(el, kind);
    el.value = imperial;
}

// Imperial: a value filled in by code shows rounded, while reading the field
// still gives the exact value behind it (so a 3.5 m rig saved from metric
// stays exactly 3.5 m, and routes use the same height as before). Once the
// user types, the field reads what they typed. Typing never goes through the
// setter. Replaced by toMetric's own accessor if the account turns metric.
function roundImperial(el, kind) {
    if (el.dataset.imperialRound === 'on') {
        el.value = el.value; // round what's already there
        return;
    }
    el.dataset.imperialRound = 'on';
    let exact = null, shown = null;
    Object.defineProperty(el, 'value', {
        configurable: true,
        get() {
            const v = native.get.call(this);
            return shown !== null && v === shown ? exact : v;
        },
        set(v) {
            if (v === '' || v === null || v === undefined || isNaN(parseFloat(v))) {
                exact = shown = null;
                native.set.call(this, v ?? '');
                return;
            }
            exact = String(v);
            shown = fmt(kind.showImperial(parseFloat(v)), kind.imperialDecimals);
            native.set.call(this, shown);
        }
    });
    el.value = native.get.call(el);
}

// Swap the unit word in the field's label: "Height (feet)" -> "Height (m)",
// or the trailing "ft" after an inline field ("Max Rig: [  ] ft").
function relabel(el) {
    for (const node of labelTextNodes(el)) {
        let text = node.nodeValue;
        for (const [re, rep] of LABEL_UNITS) text = text.replace(re, rep);
        if (text !== node.nodeValue) {
            if (!originalLabelText.has(node)) originalLabelText.set(node, node.nodeValue);
            node.nodeValue = text;
        }
    }
}

function labelTextNodes(el) {
    const label = el.labels?.[0] || el.closest('label');
    if (!label) return [];
    const nodes = [];
    const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node);
    return nodes;
}
